// Turning the on-shift preview's four gate flags into the sentence a lead reads.
//
// A pure module rather than logic inside the component, for this repository's
// standing reason: there is no DOM harness, so anything decidable without a DOM is
// decided here and tested by test/web/on-shift.test.js — the shape
// web/src/pages/admin/descriptor-form.js established.
//
// WHY THIS IS NOT A ONE-LINE TERNARY IN THE JSX. The whole task exists because
// "nobody was assigned" is currently indistinguishable from "there was nothing to
// assign". A status map with a hole in it — one member state that produces
// `undefined` — renders as an empty cell and reintroduces exactly that silence one
// level up. `memberStatus` is therefore TOTAL: every input, including a row missing
// keys entirely, produces a non-empty sentence.

/**
 * The gates in the order `ELIGIBLE_CTE_SQL` evaluates them (src/routing/assign.js).
 *
 * The order is load-bearing, not cosmetic. A member can fail several at once, and
 * the first one named has to be the first one that matters — telling a lead "off
 * shift" about someone in a queue that never auto-assigns at all sends them to
 * write a schedule that will change nothing.
 */
export const GATES = ['queue_is_assignable', 'is_active', 'can_be_assigned', 'on_shift'];

/**
 * @param {object} member  One entry of `data.members` from
 *   `GET /api/admin/queues/:id/on-shift`.
 * @returns {{eligible: boolean, gate: string|null, text: string}}
 *   `gate` is the first failing entry of `GATES`, or null when nothing failed.
 *   `text` is always a non-empty sentence.
 */
export function memberStatus(member) {
  const row = member ?? {};
  const rows = Number(row.schedule_rows);
  // `?? 0` rather than leaving NaN: a count that failed to arrive must not turn
  // into "none of their NaN shifts covers this moment".
  const scheduleRows = Number.isFinite(rows) ? rows : 0;

  if (row.queue_is_assignable === false) {
    return {
      eligible: false,
      gate: 'queue_is_assignable',
      text: 'This queue never auto-assigns (is_assignable is off).',
    };
  }
  if (row.is_active === false) {
    return { eligible: false, gate: 'is_active', text: 'Their account is not active.' };
  }
  if (row.can_be_assigned === false) {
    return {
      eligible: false,
      gate: 'can_be_assigned',
      text: 'Their Entra role cannot own tickets. Fix it in Entra, not here.',
    };
  }
  if (row.on_shift === false) {
    // NO SCHEDULE IS NOW THE COMMON CASE HERE, and it is the actionable one. It used
    // to be flagged as impossible, because the old `ON_SHIFT_SQL` began with
    // `NOT EXISTS (… agent_schedules …)` and an unscheduled agent was on shift always.
    // That fail-open is gone, so this is simply an agent nobody has configured yet —
    // and naming the fix is the whole point, because round-robin cannot pick anyone
    // in this queue until somebody does.
    if (scheduleRows === 0) {
      return {
        eligible: false,
        gate: 'on_shift',
        text: 'No schedule, so never on shift. Routing will always skip them. '
          + 'Give them a week under Admin → Agent schedules.',
      };
    }
    return {
      eligible: false,
      gate: 'on_shift',
      text: `Off shift. None of their ${scheduleRows} `
        + `${scheduleRows === 1 ? 'shift' : 'shifts'} covers this moment.`,
    };
  }

  // THE IMPOSSIBLE CASE MOVED TO THIS SIDE. `on_shift === true` with no schedule rows
  // cannot happen: the server reads both from one row of one statement, and the
  // predicate is now a bare `EXISTS (… agent_schedules …)`, so zero rows can only be
  // false. If it ever shows up, the preview and the router have stopped agreeing —
  // which is the one thing this screen exists to make impossible — so it says so
  // rather than reporting a confident "Eligible".
  if (scheduleRows === 0) {
    return {
      eligible: true,
      gate: null,
      text: 'On shift with no schedule at all, which should be impossible. '
        + 'Treat this preview as untrustworthy and check the routing query.',
    };
  }

  return {
    eligible: true,
    gate: null,
    text: `Eligible: on shift now (${scheduleRows} ${scheduleRows === 1 ? 'shift' : 'shifts'})`,
  };
}

/**
 * The headline over the member list.
 *
 * The empty case names the silence rather than describing it, because that silence
 * is the whole reason this screen was built before the schedule editor:
 * `assignFromQueue` returns null -> `routeTicket` reports `ok: true` -> the sweep
 * logs the same success line it logs when it assigns fifty.
 *
 * @param {object[]} eligible  `data.eligible`.
 * @returns {{empty: boolean, text: string}}
 */
export function eligibleSummary(eligible) {
  const list = Array.isArray(eligible) ? eligible : [];
  if (list.length === 0) {
    return {
      empty: true,
      text: 'Nobody. A ticket arriving in this queue would be created unassigned, and the '
        + 'routing sweep would log the same success line it logs when it assigns fifty.',
    };
  }
  // `display_name` explicitly, never the object: joining a list of objects printed
  // "[object Object]" on this console's list screen earlier in this plan, silently,
  // past the build and every test.
  return { empty: false, text: list.map((person) => person.display_name).join(', ') };
}
